import { getPageStatusLine } from "./page-context";

import { ActivityType, PageType, SectionType, TopicType } from "components/server-types";
import { t } from "i18n/i18n";
import {
  activitySelectors,
  courseSelectors,
  createAppSelector,
  outcomeSelectors,
  pageOutcomeSelectors,
  sectionOutcomeSelectors,
  selectCurrentSection,
  selectPagesForCurrentSection,
  termSelectors,
  topicSelectors,
} from "store/selectors";

export const TOPIC_EXPLANATION_CONTEXT =
  "A Topic is a thematic grouping of Activities within a course. Topics help organize course content into logical units and provide structure for the learning experience.";

export const ACTIVITY_EXPLANATION_CONTEXT =
  "An Activity is a collection of Pages that belong to a specific Topic. Activities can be categorized as prep, lesson, additional resources, or archive.";

export const SECTION_EXPLANATION_CONTEXT = t("ai.section_explanation", {
  returnObjects: true,
}).join("\n");

export const selectSectionMetadataContext = createAppSelector(
  [
    (state, section: SectionType) => section,
    termSelectors.selectAll,
    courseSelectors.selectAll,
    selectCurrentSection,
  ],
  (section, terms, courses, currentSection) => {
    if (!section) {
      return "";
    }

    const term = terms.find((te) => te.id === section.term_id);
    const course = courses.find((cc) => cc.id === section.course_id);
    const courseString = course ? ` in ${course.code}` : "";
    const termString = term ? ` in Term ${term.title}` : "";
    const sectionDescription = section.description;
    const courseStatusLine = `Course "${section.title}"${courseString}${termString}`;
    const currentSectionNote = currentSection?.id === section.id ? ` (currently selected)` : "";

    return `${courseStatusLine}${currentSectionNote}, described as: "${sectionDescription}"\n`;
  },
);

export const selectCourseContentSummaryContext = createAppSelector(
  [
    topicSelectors.filterForCurrentSection,
    activitySelectors.selectAll,
    (s) => selectPagesForCurrentSection(s),
    outcomeSelectors.selectAll,
    sectionOutcomeSelectors.selectAll,
    pageOutcomeSelectors.selectAll,
  ],
  (topics, activities, pages, outcomes, sectionOutcomes, pageOutcomes) => {
    const assignments = pages.filter((p) => p.category === "assignment");
    const courseResources = pages.filter((p) => p.category === "course_resource");
    const instructorWorkspace = pages.filter((p) => p.category === "instructor_workspace");

    const processedPages: PageType[] = [];
    const pageContextSelector = (_page: PageType) => {
      processedPages.push(_page);
      return `- ${getPageStatusLine(_page, pageOutcomes, sectionOutcomes, outcomes)}`;
    };

    const selectTopicStructureContext = (topic: TopicType) => {
      const topicPages = pages.filter(
        (page) =>
          activities.find((activity) => activity.id === page.activity_id)?.topic_id === topic.id,
      );

      if (!topic) {
        return "Topic not found.";
      }

      // Group pages by activity
      const activitiesMap = new Map<string, { activity: ActivityType; pages: PageType[] }>();

      topicPages.forEach((page) => {
        const activity = activities.find((a) => a.id === page.activity_id);
        if (activity) {
          if (!activitiesMap.has(activity.id)) {
            activitiesMap.set(activity.id, { activity, pages: [] });
          }
          activitiesMap.get(activity.id)!.pages.push(page);
        }
      });

      // Sort activities by order
      const sortedActivities = Array.from(activitiesMap.values()).sort(
        (a, b) => a.activity.order - b.activity.order,
      );

      if (sortedActivities.length === 0) {
        return `Topic "${topic.title}" (contains no activities or pages)`;
      }

      const activityStructures = sortedActivities.map(({ activity, pages: activityPages }) => {
        const categoryLabels = {
          prep: "Prep",
          lesson: "Lesson",
          additional_resource: "Additional Resource",
          archive: "Archive",
        };

        const categoryLabel = categoryLabels[activity.category] || activity.category;
        const activityContext = `- ${activity.title} (${categoryLabel})`;

        if (activityPages.length === 0) {
          return activityContext;
        }

        // Sort pages by order
        const sortedPages = activityPages.sort((a, b) => a.order - b.order);
        const pageContexts = sortedPages.map((page) => `  ${pageContextSelector(page)}`);

        return [activityContext, ...pageContexts].join("\n");
      });

      return [`Topic "${topic.title}" with structure:`, ...activityStructures].join("\n");
    };

    const topicItems = topics.map((topic) => selectTopicStructureContext(topic)).join("\n");
    const assignmentItems = assignments.map((a) => pageContextSelector(a)).join("\n");
    const courseResourceItems = courseResources.map((r) => pageContextSelector(r)).join("\n");
    const instructorWorkspaceItems = instructorWorkspace
      .map((i) => pageContextSelector(i))
      .join("\n");
    const hasInstructorWorkspace = instructorWorkspaceItems.length > 0;

    return {
      pages: processedPages,
      context: [
        t("ai.section_content_summary"),
        t("ai.topics_list"),
        topicItems,
        t("ai.assignments_list"),
        assignmentItems,
        t("ai.course_resources_list"),
        courseResourceItems,
        hasInstructorWorkspace ? t("ai.instructor_workspace_list") : "",
        hasInstructorWorkspace ? instructorWorkspaceItems : "",
      ]
        .filter(Boolean)
        .join("\n"),
    };
  },
);
